import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ForCompanies() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 sm:pt-40 lg:pt-56 pb-12 sm:pb-16 lg:pb-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight">
            Support critical people — without building programs.
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-6 sm:mb-8 font-normal leading-relaxed">
            Halvera allows organizations to support roles where disruption carries real cost — without introducing new systems, policies, or administrative overhead.
          </p>
          <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 font-normal leading-relaxed">
            Instead of managing exceptions, reimbursements, or ad-hoc fixes, companies assign access to a single continuity layer.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-gray-900 font-semibold">
            When plans break, Halvera handles it.
          </p>
        </div>
      </section>

      {/* Why Companies Use Halvera */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
            Why Companies Use Halvera
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 lg:mb-12 text-center font-normal leading-relaxed">
            Organizations choose Halvera because it keeps support predictable, contained, and quiet.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                Predictable annual cost
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal leading-relaxed">
                No variable spend. No surprise reimbursements.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                No payroll or tax exposure
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal leading-relaxed">
                Access is sponsored, not compensated.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                No expense-policy rewrites
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal leading-relaxed">
                Existing policies remain intact.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                No vendor sprawl
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal leading-relaxed">
                One system instead of multiple providers.
              </p>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200 sm:col-span-2 lg:col-span-1">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                No new internal processes
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-normal leading-relaxed">
                No training, tooling, or workflow changes.
              </p>
            </div>
          </div>
          <p className="text-base sm:text-lg text-gray-700 mt-8 sm:mt-10 lg:mt-12 text-center font-normal leading-relaxed">
            Halvera is designed to keep company costs fixed and administrative overhead minimal.
          </p>
        </div>
      </section>

      {/* Where Halvera Fits */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
            Where Halvera Fits
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 text-center font-normal leading-relaxed">
            Halvera operates alongside existing travel, HR, and expense systems.
          </p>
          <div className="bg-gray-50 p-6 sm:p-8 lg:p-10 rounded-lg mb-8 sm:mb-10">
            <p className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
              It does not:
            </p>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></div>
                <span className="text-sm sm:text-base text-gray-700 font-normal">enforce policy</span>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></div>
                <span className="text-sm sm:text-base text-gray-700 font-normal">replace booking tools</span>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mt-2.5 flex-shrink-0"></div>
                <span className="text-sm sm:text-base text-gray-700 font-normal">manage spend</span>
              </li>
            </ul>
          </div>
          <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 text-center font-normal leading-relaxed">
            Instead, it provides a private escalation and continuity layer when standard systems fail — during disruption, complexity, or high-stakes moments.
          </p>
          <p className="text-base sm:text-lg text-gray-900 font-semibold text-center">
            This keeps problems from cascading upward or outward.
          </p>
        </div>
      </section>

      {/* The Result */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 lg:mb-12 text-center">
            The Result
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 text-center font-semibold">
            Companies see:
          </p>
          <ul className="space-y-5 sm:space-y-6 mb-10 sm:mb-12 lg:mb-16">
            <li className="flex items-start gap-4 sm:gap-5">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-base sm:text-lg text-gray-700 font-normal leading-relaxed">
                Less chaos during disruptions
              </span>
            </li>
            <li className="flex items-start gap-4 sm:gap-5">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-base sm:text-lg text-gray-700 font-normal leading-relaxed">
                Fewer executive fire drills
              </span>
            </li>
            <li className="flex items-start gap-4 sm:gap-5">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-base sm:text-lg text-gray-700 font-normal leading-relaxed">
                Cleaner support for high-impact roles
              </span>
            </li>
            <li className="flex items-start gap-4 sm:gap-5">
              <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-base sm:text-lg text-gray-700 font-normal leading-relaxed">
                A system that scales quietly as the organization grows
              </span>
            </li>
          </ul>
          <div className="text-center space-y-2 sm:space-y-3 text-base sm:text-lg text-gray-900 font-normal">
            <p>Nothing new to manage.</p>
            <p>Nothing new to explain.</p>
          </div>
        </div>
      </section>

      {/* How Companies Use Access */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
            How Companies Use Access
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-4 sm:mb-6 text-center font-normal leading-relaxed">
            Organizations don't buy features.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-8 sm:mb-10 text-center font-semibold">
            They assign responsibility.
          </p>
          <div className="bg-gray-50 p-6 sm:p-8 lg:p-10 rounded-lg mb-8 sm:mb-10">
            <p className="text-sm font-semibold text-gray-900 mb-4 sm:mb-6 uppercase tracking-wide">
              Typical structure:
            </p>
            <ul className="space-y-4 sm:space-y-5">
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Core</p>
                  <p className="text-sm sm:text-base text-gray-600 font-normal">general employees</p>
                </div>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Reserve</p>
                  <p className="text-sm sm:text-base text-gray-600 font-normal">leaders and key contributors</p>
                </div>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Ascend</p>
                  <p className="text-sm sm:text-base text-gray-600 font-normal">executives, partners, critical roles</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="text-center space-y-2 mb-8 sm:mb-10 text-base sm:text-lg text-gray-700 font-normal">
            <p>One system.</p>
            <p>Clear standards.</p>
            <p>No complexity.</p>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 sm:mb-8">
            Getting Started
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-10 font-normal leading-relaxed max-w-2xl mx-auto">
            Company access is reviewed to ensure fit.
          </p>
          <p className="text-base sm:text-lg text-gray-300 mb-10 sm:mb-12 font-normal leading-relaxed max-w-2xl mx-auto">
            If there's alignment, Halvera works with you to determine appropriate access levels and next steps.
          </p>
          <Link 
            to="/apply" 
            className="inline-flex items-center px-8 sm:px-10 py-3 sm:py-4 bg-red-600 text-white rounded-md text-sm sm:text-base font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
          >
            Request Company Access
            <i className="ri-arrow-right-line ml-2 text-lg"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
